/* eslint
    @typescript-eslint/no-shadow: "warn",
    no-restricted-syntax: ["warn", "ForOfStatement"],
    no-else-return: "warn",
    prefer-const: "warn",
    prefer-arrow-callback: "warn",
    no-empty: "warn"
*/

import { getCookie } from '@shared/easyfy.core';
import {parseToBoolean} from '../easyfy.utils';
import {LocalStorageKeys} from "./easyfy.eventhandler";

declare global {
  interface Window {
    EasyfyEventLayer: any,
    EasyfyEventLayerInstance: any;
    isEditModeEnabled?: boolean;
    isAdminModeEnabled?: boolean;
    blazorFinishedLoading?: boolean;
    EasyfyMethods: any;
  }
}

export interface IDataMapper {
  MapEventData(event: IEasyfyEvent): any;
}

export interface IEasyfyEventExporter {
  mapper: IDataMapper;

  WithMapper(mapper: IDataMapper): void;

  RegisterEvent(event: IEasyfyEvent): Promise<any>;
}

export interface IEasyfyEvent {
  eventName: string;
  data: any;
}

export interface IEasyfyEventService {
  name: string;
  includeAllEvents: boolean;
  events: string;
  enabled: boolean;
}

export interface IEasyfySiteInfo {
  currencyCode: string;
  checkoutPath: string;
}

export interface IEasyfyPageInfo {
  PageType: string;
  ViewName: string;
}

export interface IProductData {
  Id: any;
  DomainId: any;
  Ean: any;
  DisplayName: any;
  Brand: any;
  Categories: any[];
  Price: number;
  PriceExVat: number;
  CampaignCode: string;
  AbsoluteProductUrl: string;
  RelativeProductUrl: string;
  ProductImageUrl: string;
  SponsorshipId: string;
}

export interface IProductSalesUnitPrice {
  salesUnitsCode: string;
  packSize: number;
  priceExVat: number;
  priceIncVat: number;
}

export interface IBasket {
  priceIncVat: number;
  priceExVat: number;
  priceIncVatExShipping: number;
  vat: number;
  rows: IBasketRow[];
}

export interface IBasketRow {
  productIdentifier: string;
  qty: number;
  salesUnitCode: string;
}

export interface IProductList {
  ListName: string;
  ProductIds: string[];
}

export interface IBannerData {
  Id: any;
  DisplayName: any;
  PageUrl: any;
  LinkUrl: any;
}

export interface ISearchPerformedData {
  searchString: string;
  source: string;
  productIdentifiers: any[];
  productHits: number;
  articleHits: number;
  brandHits: number;
  pageHits: number;
  totalProductResults: number;
  suggestions: any[];
}

export interface IPageViewData {
  pageId: string;
  searchPageId: string;
}

export interface IQuantityChangedData {
  productIdentifier: string;
  listName: string;
  salesUnitCode: string;
  oldQuantity: number;
  newQuantity: number;
}

export interface ISessionStarted {
  Culture: string;
  Currency: string;
  IsDesktop: boolean;
  IsMobilePhone: boolean;
  IsTablet: boolean;
  RecurringUser: boolean;
  Referrer: string;
  Path: string;
  UserAgent: string;
  SessionId: string;
  ClientId: string;
  UserId: string;
  VisitorId: string;
}

export interface IWorkerJob {
  readonly Type: string;
  Data: any;
  Runs: number;
  MaxRuns: number;

  RunJob(data: any): Promise<any>;

  AnonymizeWithDbId(db_id: any): any;
}

export interface IWorkerMessage {
  Message: string;
  Data: any;
}

export interface ITrackableElement {
  Id: any;
  Type: any;
  Properties: Map<string, any>;
}

export interface IClientEventMetaData {
  ClientId: string;
  UserId: string;
  SessionId: string;
  IsDesktop: boolean;
  IsMobilePhone: boolean;
  IsTablet: boolean;
  IPAddress: string;
}

export interface IStatsEventData {
  ClientMetaData: IClientEventMetaData;
  EventRequests: any[];
}

export module EasyfyEventData {
  export function AddDataToEventLayer(data: any): void {
    if (data) {
      window.EasyfyEventLayer = window.EasyfyEventLayer || {};
      data.events.forEach((event: any) => {
        if (!window.EasyfyEventLayer.events) {
          window.EasyfyEventLayer.events = [];
        }
        window.EasyfyEventLayer.events.push(event);
      });

      window.EasyfyEventLayer.products = window.EasyfyEventLayer.products || {};

      for (const product in data.products) {
        if (!window.EasyfyEventLayer.products || window.EasyfyEventLayer.products[product]) continue;
        window.EasyfyEventLayer.products[product] = data.products[product];
      }

      window.EasyfyEventLayer.productLists = window.EasyfyEventLayer.productLists || {};

      for (const productList in data.productLists) {
        if (!window.EasyfyEventLayer.productLists || window.EasyfyEventLayer.productLists[productList]) continue;
        window.EasyfyEventLayer.productLists[productList] = data.productLists[productList];
      }

      window.EasyfyEventLayer.productPrices = window.EasyfyEventLayer.productPrices || {};

      for (const price in data.productPrices) {
        if (!window.EasyfyEventLayer.productPrices || window.EasyfyEventLayer.productPrices[price]) continue;
        window.EasyfyEventLayer.productPrices[price] = data.productPrices[price];
      }

      window.EasyfyEventLayer.basket = window.EasyfyEventLayer.basket || {};

      if (data.basket) {
        window.EasyfyEventLayer.basket = data.basket;
      }
    }
  }

  export function AddCustomDataToEventLayer(data: Map<string, object>): void {

    if (!data) return;

    if (!window.EasyfyEventLayer.customData) {
      window.EasyfyEventLayer.customData = {};
    }
    data.forEach((value: object, key: string) => {
      window.EasyfyEventLayer.customData[key] = value;
    });
  }

  export function GlobalIgnoreEvents(): boolean {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: boolean;
    if (window.EasyfyEventLayer.configuration?.addProductImpressionsServerSide != undefined) {
      result = (window.EasyfyEventLayer.configuration.addProductImpressionsServerSide as boolean) === false;
    }

    return result;
  }

  export function SiteInfo(): IEasyfySiteInfo {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IEasyfySiteInfo;
    if (window.EasyfyEventLayer.site) {
      result = window.EasyfyEventLayer.site as IEasyfySiteInfo;
    }

    return result;
  }

  export function PageInfo(): IEasyfyPageInfo {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IEasyfyPageInfo;
    if (window.EasyfyEventLayer.pageInfo) {
      result = window.EasyfyEventLayer.pageInfo as IEasyfyPageInfo;
    }

    return result;
  }

  export function Events(): IEasyfyEvent[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IEasyfyEvent[] = [];
    if (window.EasyfyEventLayer.events) {
      result = window.EasyfyEventLayer.events as IEasyfyEvent[];
    }

    return result;
  }

  export function ClearEvents(): void {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    if (window.EasyfyEventLayer.events) {
      window.EasyfyEventLayer.events = [];
    }
  }

  export function ShouldRegisterProductImpressions(): boolean {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: boolean;
    if (window.EasyfyEventLayer.configuration?.addProductImpressionsServerSide != undefined) {
      result = (window.EasyfyEventLayer.configuration.addProductImpressionsServerSide as boolean) === false;
    }

    return result;
  }

  export function ShouldRegisterBannerImpressions(): boolean {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: boolean;
    if (window.EasyfyEventLayer.configuration?.addBannerImpressionsServerSide != undefined) {
      result = (window.EasyfyEventLayer.configuration.addBannerImpressionsServerSide as boolean) === false;
    }

    return result;
  }

  export function GetAllServices(): IEasyfyEventService[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IEasyfyEventService[] = [];
    if (window.EasyfyEventLayer?.configuration?.services) {
      result = window.EasyfyEventLayer.configuration.services as IEasyfyEventService[];
    }

    return result;
  }

  export function GetService(serviceName: string): IEasyfyEventService {
    return EasyfyEventData.GetAllServices().find(serviceInstance => serviceInstance.name.toLowerCase() === serviceName.toLowerCase());
  }

  export function GetServiceNames(): string[] {
    const result: string[] = [];
    EasyfyEventData.GetAllServices().forEach(service => result.push(service.name));
    return result;
  }

  export function ServiceIsActive(serviceName: string): boolean {
    const service = EasyfyEventData.GetAllServices().find(serviceInstance => serviceInstance.name.toLowerCase() === serviceName.toLowerCase());
    return (service && service.enabled);
  }

  export function ConsumesEvent(serviceName: string, eventName: string): boolean {
    const service = EasyfyEventData.GetService(serviceName);
    if (!service || !service.enabled) return false;
    if (service.includeAllEvents) {
      return true;
    }

    const serviceConsumables = service.events?.trim().replace(/\s/g, '').split(",");

    // TODO: Check the intention of this code. I guess it should be able to be false.
    return serviceConsumables.find(event => event.trim().toLowerCase() === eventName.toLowerCase()) != undefined ?? false;

  }

  export function Product<T extends IProductData>(id: string): T | undefined {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: T = null;
    if (window.EasyfyEventLayer.products) {
      result = window.EasyfyEventLayer.products[id] as T;
    }
    return result;
  }

  export function ProductAt<T extends IProductData>(atIndex: number): T | undefined {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: T = null;
    try {
      if (window.EasyfyEventLayer.products) {
        result = window.EasyfyEventLayer.products[atIndex] as T;
      }
    } catch (error) {
      console.error("Error accessing product at index:", atIndex, error);
    }

    return result;
  }

  export function Products<T extends IProductData>(ids: string[]): T[] {
    const productDataSet: T[] = [];
    ids.forEach((id) => {
      productDataSet.push(EasyfyEventData.Product<T>(id));
    });
    return productDataSet;
  }

  export function ProductsAt<T extends IProductData>(indexes: number[]): T[] {
    const productDataSet: T[] = [];
    indexes.forEach((index) => {
      productDataSet.push(EasyfyEventData.ProductAt<T>(index));
    });
    return productDataSet;
  }

  export function AllProducts<T extends IProductData>(): T[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: T[] = [];
    if (window.EasyfyEventLayer.products) {
      result = window.EasyfyEventLayer.products as T[];
    }
    return result;
  }

  export function AllProductLists(): IProductList[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result = [];
    if (window.EasyfyEventLayer.productLists) {
      result = window.EasyfyEventLayer.productLists;
    }
    return result;
  }

  export function ProductIndexInList(listName: string, productId: string): number | null {
    if (!listName || !productId) return null;
    const productsInList = window.EasyfyEventLayer.productLists[listName];
    if (!productsInList) return null;
    return productsInList.indexOf(productId);
  }

  export function ProductSalesUnitPrice(id: string): IProductSalesUnitPrice | undefined {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IProductSalesUnitPrice = null;

    if (window.EasyfyEventLayer.productPrices) {
      result = window.EasyfyEventLayer.productPrices[id] as IProductSalesUnitPrice;
    }

    return result;
  }

  export function ProductSalesUnitPrices(ids: string[]): IProductSalesUnitPrice[] | undefined {
    const prices: IProductSalesUnitPrice[] = [];
    ids.forEach((id) => {
      prices.push(EasyfyEventData.ProductSalesUnitPrice(id));
    });
    return prices;
  }

  export function AllProductSalesUnitPrices(): IProductSalesUnitPrice[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IProductSalesUnitPrice[] = [];

    if (window.EasyfyEventLayer.productPrices) {
      result = window.EasyfyEventLayer.productPrices as IProductSalesUnitPrice[];
    }

    return result;
  }

  export function Banner(id: string): IBannerData | undefined {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IBannerData = null;
    if (window.EasyfyEventLayer.banners) {
      result = window.EasyfyEventLayer.banners[id] as IBannerData;
    }
    return result;
  }

  export function AllBanners(): IBannerData[] {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    let result: IBannerData[] = [];
    if (window.EasyfyEventLayer.banners) {
      result = window.EasyfyEventLayer.banners as IBannerData[];
    }
    return result;
  }

  export function HasEvents(): boolean {
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    return (window.EasyfyEventLayer && window.EasyfyEventLayer.events?.length > 0);
  }

  export function Basket(): IBasket {
    window.EasyfyEventLayer.basket = window.EasyfyEventLayer.basket || {};
    let result: IBasket;
    if (window.EasyfyEventLayer.basket) {
      result = window.EasyfyEventLayer.basket as IBasket;
    }
    return result;
  }

  export function GetCurrencyCode(): string {
    let returnValue = null;
    window.EasyfyEventLayer = window.EasyfyEventLayer || {};
    if (window.EasyfyEventLayer && window.EasyfyEventLayer.site) {
      returnValue = window.EasyfyEventLayer.site.currencyCode as string;
    }

    return returnValue;
  }

  export function GetCustomData(key: string): any {
    if (!window.EasyfyEventLayer.customData) {
      window.EasyfyEventLayer.customData = {};
    }
    return window.EasyfyEventLayer.customData[key];
  }

  export function GetClientEventMetaData(): IClientEventMetaData {
    return {
      SessionId: localStorage.getItem(LocalStorageKeys.SessionId),
      UserId: localStorage.getItem(LocalStorageKeys.UserId),
      ClientId: localStorage.getItem(LocalStorageKeys.ClientId),
      IsDesktop: parseToBoolean(localStorage.getItem(LocalStorageKeys.IsDekstop)),
      IsMobilePhone: parseToBoolean(localStorage.getItem(LocalStorageKeys.IsMobilePhone)),
      IsTablet: parseToBoolean(localStorage.getItem(LocalStorageKeys.IsTablet)),
      IPAddress: localStorage.getItem(LocalStorageKeys.IPAddress)
    };
  }

  export function IsRecurringUser(flagAsRecurringAfterCheck: boolean): boolean {
    const cookieKey = localStorage.getItem(LocalStorageKeys.UserIdCookieKey);
    const userId = cookieKey ? getCookie(cookieKey) : undefined;
    const lastCheckedUserId = localStorage.getItem(LocalStorageKeys.LastCheckedUserId);
    let result = false;

    if (userId === lastCheckedUserId) {
      result = true;
    }

    if (flagAsRecurringAfterCheck && userId) {
      localStorage.setItem(LocalStorageKeys.LastCheckedUserId, userId);
    }

    return result;
  }

  export function SetStatsApiBlock(block: boolean) {
    if (block) {
      localStorage.setItem(LocalStorageKeys.StatsIsBlocked, "1");
    } else {
      localStorage.removeItem(LocalStorageKeys.StatsIsBlocked);
    }
  }

  export function StatsApiIsBlocked(): boolean {
    const blockedFlag = localStorage.getItem(LocalStorageKeys.StatsIsBlocked);

    if (blockedFlag) {
      return true;
    }
    return false;
  }
}
